// linkSeed — shared helpers for seeding a Link chat with a referenced domain
// entity. Used both by LinkLauncherContext (askLink, from an entity detail page)
// and by the in-session "Öppna objekt" overlay (#254, from Link's + menu).
//
// The seed = a VISIBLE opener + a HIDDEN instruction block wrapped in ctx
// sentinels. The sentinels are stripped from the user's bubble on render
// (stripInjectedContext in LinkAgentModal) but still reach the model and are
// persisted (searchable — powers #225 continue-or-new).

import type { LinkAgentReference } from '@/lib/api/linkAgentApi';

// Injected-context sentinels — kept in sync with CTX_OPEN/CTX_CLOSE in
// LinkAgentModal.tsx and InjectedContextOpen/Close in go.nexilink.
export const CTX_OPEN = '<<<ctx>>>';
export const CTX_CLOSE = '<<</ctx>>>';

// Generic fallback when a caller doesn't supply its own instruction.
export const DEFAULT_INSTRUCTION =
    'Börja kort: hälsa och fråga vad användaren vill göra — t.ex. redigera, diskutera eller förbättra. Sammanfatta inte allt oombedt. Vid redigering: emittera det som ett redigerbart utkast med emit_artifact.';

export interface SeedEntityOpts {
    reference: LinkAgentReference;
    refLabel: string;
    seed: string;
    instruction?: string;
}

// Build the seed text: the visible opener + a hidden ctx block carrying (a) the
// how-to-act instruction, (b) a searchable `<type>_id=<id>` marker for #225
// lookups, and (c) a compact @@ref@@ token the breadcrumb parses.
export function buildSeededText(opts: SeedEntityOpts): string {
    const { reference, refLabel, seed, instruction } = opts;
    const idTag = `${reference.type}_id=${reference.id}`;
    const safeName = refLabel.replace(/[:@\n<>]/g, ' ').trim();
    const refToken = `@@ref:${reference.type}:${reference.id}:${safeName}@@`;
    const hidden = `${CTX_OPEN}\n[${instruction ?? DEFAULT_INSTRUCTION} (${idTag})]\n${refToken}\n${CTX_CLOSE}`;
    return `${seed}\n${hidden}`;
}

// "Open this object as an editable draft" seed — emits the entity DIRECTLY as an
// artifact (no ask-first), then invites edits. Matches the Wave 6 need/module
// buttons on the entity detail pages. Employees are intentionally unsupported
// here (a person isn't an editable artifact).
export function entityOpenSeed(
    type: 'need' | 'module',
    label: string,
): { seed: string; instruction: string } {
    if (type === 'module') {
        return {
            seed: `Jag vill jobba vidare med modulen "${label}".`,
            instruction:
                'Hämta modulen med get_module och emittera den DIREKT som ett redigerbart utkast med emit_artifact så användaren ser den i förhandsgranskningen — beskriv den inte bara. Fråga sedan kort vad de vill göra: redigera, diskutera eller förbättra.',
        };
    }
    return {
        seed: `Jag vill jobba vidare med annonsen "${label}".`,
        instruction:
            'Hämta annonsen med get_need och emittera den DIREKT som ett redigerbart utkast med emit_artifact så användaren ser den i förhandsgranskningen — beskriv den inte bara. Fråga sedan kort vad de vill göra: redigera, diskutera eller förbättra.',
    };
}
